import * as React from 'react';
import { History } from 'history';
import { Loader } from 'Src/components/Loader/Loader';
import {
  createSuperAdminPostcode,
  getSuperAdminPostcode,
  SuperAdminPostcode,
  SuperAdminPostcodeSaveData,
  updateSuperAdminPostcode
} from 'Src/helpers/service/superadmin/postcodes';

const REGION_OPTIONS = [
  { value: 'GB', label: 'GB' },
  { value: 'US', label: 'US' },
  { value: 'DE', label: 'DE' },
  { value: 'ES', label: 'ES' },
  { value: 'AE', label: 'AE' }
];

interface Props {
  history: History;
  match: {
    params: {
      postcodeId?: string;
    };
  };
}

interface PostcodeDraft {
  postcode: string;
  region: string;
  lat: string;
  lng: string;
}

interface State {
  isLoading: boolean;
  isSaving: boolean;
  errorMessage: string;
  draft: PostcodeDraft;
}

function getDefaultDraft(): PostcodeDraft {
  return {
    postcode: '',
    region: 'GB',
    lat: '',
    lng: ''
  };
}

function buildDraftFromPostcode(postcode: SuperAdminPostcode): PostcodeDraft {
  return {
    postcode: postcode.postcode || '',
    region: postcode.region || 'GB',
    lat: postcode.point && typeof postcode.point.lat === 'number' ? String(postcode.point.lat) : '',
    lng: postcode.point && typeof postcode.point.lng === 'number' ? String(postcode.point.lng) : ''
  };
}

function buildPayload(draft: PostcodeDraft): SuperAdminPostcodeSaveData {
  return {
    postcode: draft.postcode.trim(),
    region: draft.region,
    point: {
      lat: Number(draft.lat),
      lng: Number(draft.lng)
    }
  };
}

export class SuperAdminPostcodeForm extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      isLoading: false,
      isSaving: false,
      errorMessage: '',
      draft: getDefaultDraft()
    };
  }

  componentDidMount() {
    const postcodeId = this.getPostcodeId();

    if (postcodeId) {
      this.setState({ isLoading: true, errorMessage: '' });

      getSuperAdminPostcode(postcodeId)
        .then(postcode => {
          this.setState({
            isLoading: false,
            draft: buildDraftFromPostcode(postcode)
          });
        })
        .catch(error => {
          console.error('Can not load postcode', error);
          this.setState({
            isLoading: false,
            errorMessage: 'Can not load postcode. Please check the session and try again.'
          });
        });
    }
  }

  getPostcodeId(): string | undefined {
    return this.props.match.params.postcodeId;
  }

  isEditMode(): boolean {
    return typeof this.getPostcodeId() !== 'undefined';
  }

  onInputChange = (field: keyof PostcodeDraft, event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const value = event.target.value;

    this.setState(prevState => ({
      draft: {
        ...prevState.draft,
        [field]: value
      }
    }));
  };

  onBackClick = () => {
    this.props.history.push('/postcodes');
  };

  validate(): string {
    const { draft } = this.state;

    if (draft.postcode.trim() === '') {
      return 'Postcode is required.';
    }

    if (draft.lat.trim() === '' || isNaN(Number(draft.lat))) {
      return 'Latitude should be a number.';
    }

    if (draft.lng.trim() === '' || isNaN(Number(draft.lng))) {
      return 'Longitude should be a number.';
    }

    const lat = Number(draft.lat);
    const lng = Number(draft.lng);

    if (lat < -90 || lat > 90) {
      return 'Latitude should be between -90 and 90.';
    }

    if (lng < -180 || lng > 180) {
      return 'Longitude should be between -180 and 180.';
    }

    return '';
  }

  onSaveClick = () => {
    const validationError = this.validate();

    if (validationError) {
      this.setState({ errorMessage: validationError });
      return;
    }

    const postcodeId = this.getPostcodeId();
    const payload = buildPayload(this.state.draft);

    this.setState({ isSaving: true, errorMessage: '' });

    const savePromise =
      this.isEditMode() && postcodeId
        ? updateSuperAdminPostcode(postcodeId, payload)
        : createSuperAdminPostcode(payload);

    savePromise
      .then(() => {
        this.props.history.push('/postcodes');
      })
      .catch(error => {
        console.error('Can not save postcode', error);
        this.setState({
          isSaving: false,
          errorMessage: 'Can not save postcode. Please check the fields and try again.'
        });
      });
  };

  render() {
    const { draft, errorMessage, isLoading, isSaving } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    return (
      <div className="container-fluid py-4">
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h2 className="mb-1">{this.isEditMode() ? 'Edit postcode' : 'Add postcode'}</h2>
              <div className="text-muted">Postcode location and region settings.</div>
            </div>
            <button className="btn btn-outline-secondary" onClick={this.onBackClick} disabled={isSaving}>
              Back to postcodes
            </button>
          </div>

          {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}

          <div className="card">
            <div className="card-body">
              <div className="row">
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Postcode</label>
                    <input
                      type="text"
                      className="form-control"
                      value={draft.postcode}
                      onChange={event => this.onInputChange('postcode', event)}
                      placeholder="PE13 1AA"
                    />
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="form-group">
                    <label>Region</label>
                    <select
                      className="form-control"
                      value={draft.region}
                      onChange={event => this.onInputChange('region', event)}
                    >
                      {REGION_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-3">
                  <div className="form-group">
                    <label>Latitude</label>
                    <input
                      type="text"
                      className="form-control"
                      value={draft.lat}
                      onChange={event => this.onInputChange('lat', event)}
                      placeholder="52.664"
                    />
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="form-group">
                    <label>Longitude</label>
                    <input
                      type="text"
                      className="form-control"
                      value={draft.lng}
                      onChange={event => this.onInputChange('lng', event)}
                      placeholder="0.159"
                    />
                  </div>
                </div>
              </div>

              <div className="d-flex justify-content-end">
                <button className="btn btn-outline-secondary mr-2" onClick={this.onBackClick} disabled={isSaving}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={this.onSaveClick} disabled={isSaving}>
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
